import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilSimple, Sparkle, Stamp, Trash } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ConfirmDialog';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import { deleteTemplate, listTemplates, type Template } from '@/api/templates';

interface Props {
  slug: string;
  onSelect: (templateId: string) => void;
  // Optional: when set, the empty-state surfaces the AI generator as the
  // primary CTA next to "Ny mall" so brand-new orgs aren't dead-ended.
  onGenerate?: () => void;
}

// TemplatesView is the table of saved templates. Create flow lives one
// level up in Schedule.tsx so the "+ Ny mall" button can sit in the
// toolbar with the view tabs — this component is just the list.
//
// Layout uses CSS grid rather than a <table> to match the Members page
// pattern: easier sticky-right RowActions cell, easier horizontal
// scrolling on narrow viewports, easier per-row hover state.
export function TemplatesView({ slug, onSelect, onGenerate }: Props) {
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const { t } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates', slug],
    queryFn: () => listTemplates(token, slug),
    enabled: !!slug,
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTemplate(token, slug, id, ''),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates', slug] }),
  });

  async function onDelete(tplId: string, tplName: string) {
    const ok = await confirm({
      title: t('templates.deleteConfirmTitle'),
      body: t('templates.deleteConfirmBody', { name: tplName }),
      confirmLabel: t('templates.deleteTemplate'),
      tone: 'destructive',
    });
    if (!ok) return;
    deleteMut.mutate(tplId);
  }

  if (isLoading) {
    return <p className="text-sm text-foreground/60">{t('common.loading')}</p>;
  }

  if (templates.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <Stamp weight="duotone" className="mx-auto mb-3 size-10 text-foreground/40" />
        <h2 className="font-heading text-lg font-bold">{t('templates.empty.title')}</h2>
        <p className="mt-1 text-sm text-foreground/70">{t('templates.empty.body')}</p>
        {onGenerate && (
          <div className="mt-5 flex justify-center">
            <Button onClick={onGenerate}>
              <Sparkle weight="bold" />
              {t('templateGenerate.toolbarCta')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div
        role="table"
        className="grid grid-cols-[1fr_140px_64px] divide-y divide-border"
      >
        <div
          role="rowgroup"
          className="contents bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground"
        >
          <div role="row" className="contents">
            <div role="columnheader" className="px-4 py-2.5 font-heading font-semibold">
              {t('templates.column.name')}
            </div>
            <div role="columnheader" className="px-4 py-2.5 font-heading font-semibold">
              {t('templates.column.slots')}
            </div>
            <div
              role="columnheader"
              className="sticky right-0 z-10 bg-muted/40 px-2 text-right"
            >
              <span className="sr-only">{t('templates.column.actions')}</span>
            </div>
          </div>
        </div>

        <div role="rowgroup" className="contents">
          {templates.map((tpl) => (
            <TemplateRow
              key={tpl.id}
              template={tpl}
              onOpen={() => onSelect(tpl.id)}
              onDelete={() => onDelete(tpl.id, tpl.name)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  template: Template;
  onOpen: () => void;
  onDelete: () => void;
}

function TemplateRow({ template, onOpen, onDelete }: RowProps) {
  const { t } = useTranslation();

  // Both menu and fast-action expose Edit (= open) + Delete. The whole
  // row is a button via the Open action, but the explicit menu entry
  // makes the affordance discoverable for keyboard users.
  const menuActions: RowAction[] = [
    {
      id: 'edit',
      label: t('templates.editTemplate'),
      icon: PencilSimple,
      onSelect: onOpen,
    },
    {
      id: 'delete',
      label: t('templates.deleteTemplate'),
      icon: Trash,
      onSelect: onDelete,
      destructive: true,
    },
  ];
  const fastActions: RowAction[] = [
    {
      id: 'edit',
      label: t('templates.editTemplate'),
      icon: PencilSimple,
      onSelect: onOpen,
    },
  ];

  return (
    <div
      role="row"
      // group/template lets RowActions' inner hover treatment compose
      // with the row's own hover state without colliding with members'
      // existing `group/nav` etc.
      className="group/template contents cursor-pointer text-sm transition-colors hover:bg-muted/30"
      onClick={onOpen}
    >
      <div role="cell" className="px-4 py-3 font-heading font-semibold text-foreground">
        {template.name}
      </div>
      <div role="cell" className="px-4 py-3 text-foreground/70">
        {t('templates.slotsCount', { count: template.slots.length })}
      </div>
      <div
        role="cell"
        // stopPropagation so clicks on the menu/buttons don't bubble up
        // to the row-open handler.
        onClick={(e) => e.stopPropagation()}
        className="sticky right-0 z-10 flex items-center justify-end bg-card pr-2 group-hover/template:bg-[color-mix(in_oklch,var(--color-card)_70%,var(--color-muted)_30%)]"
      >
        <RowActions fastActions={fastActions} menuActions={menuActions} />
      </div>
    </div>
  );
}
